// src/api/setting.js
import patientListService from '@/services/patientListService';
import axios from 'axios';
import { getServiceConfigHeaders } from "@/utils/requestConfig";
import { CLOUD_SERVICE_BASE_URL } from "@/utils/const";
import { transSericeApolloClient } from '@/graphql/client';
import { GET_PRACTICE_INFO_SETTINGS } from '@/graphql/queries/settings';

// For va-order-test compatibility
export async function getSettingData(url) {
  const config = await getServiceConfigHeaders();
  const baseUrl = CLOUD_SERVICE_BASE_URL + (process.env.VUE_APP_ENV === "production"
    ? "/portal/trans-service"
    : "/portal/trans-service-st"
  );

  return axios.get(baseUrl + url, {
    headers: config
  });
}

/**
 * Get list of doctors/customers by clinic ID
 * @param {string} clinicId - The clinic ID from userInfo
 * @returns {Promise} - Promise with list of doctors/customers
 */
export const getCustomersByClinicIdApi = (clinicId) => {
  return patientListService.get(`/setting/list-customer-by-id/${clinicId}`);
};

/**
 * Get the current timezone setting for the provider.
 * customer_id and clinic_id are resolved from the JWT.
 * @returns {Promise} Promise resolving to { timezone: string } (defaults to "PST")
 */
export const getTimezoneSettingApi = () => {
  return patientListService.get('/setting/getTimezoneSetting');
};

/**
 * Update the timezone setting for the provider.
 * @param {Object} data - { timezone: string, old_timezone: string }
 * @returns {Promise} Promise resolving to { timezone: string }
 */
export const setTimezoneSettingCustomerApi = (data) => {
  return patientListService.post('/setting/setTimezoneSettingCustomer', data);
};

/**
 * Get billing settings (practice and personal)
 * Returns both practice and personal consultation fee settings
 * @returns {Promise} - Promise with { practice: {...}, personal: {...} }
 */
export const getBillingSettingApi = () => {
  return patientListService.get('/setting/getBillingSetting');
};

/**
 * Get billing apply settings
 * Returns which settings apply to all providers or use practice settings
 * @returns {Promise} - Promise with apply settings like { billing_consultation_fee: { apply_to_all, use_practice } }
 */
export const getBillingApplySettingApi = () => {
  return patientListService.get('/setting/getBillingApplySetting');
};

/**
 * Save personal billing settings (for customer/provider)
 * @param {Object} data - The billing settings to save
 * @returns {Promise}
 */
export const setBillingSettingCustomerApi = (data) => {
  return patientListService.post('/setting/setBillingSettingCustomer', data);
};

/**
 * Save practice/clinic billing settings (for admin)
 * @param {Object} data - The billing settings to save
 * @returns {Promise}
 */
export const setBillingSettingClinicApi = (data) => {
  return patientListService.post('/setting/setBillingSettingClinic', data);
};

/**
 * Fetch clinic details including practice information (personal and practice)
 * @param {Object} params - Query parameters
 * @param {number} params.customer_id - Customer ID (optional, defaults to null)
 * @param {number} params.clinic_id - Clinic ID (optional, defaults to null)
 * @returns {Promise} Promise with clinic details including practiceInfoPersonal and practiceInfoPractice
 */
export const fetchClinicDetailsApi = async ({ customer_id = null, clinic_id = null } = {}) => {
  try {
    const response = await transSericeApolloClient.query({
      query: GET_PRACTICE_INFO_SETTINGS,
      variables: { 
        customer_id: String(customer_id), 
        clinic_id: String(clinic_id) 
      },
      fetchPolicy: 'network-only'
    });

    // Check for GraphQL errors (GraphQL can return 200 with errors)
    if (response.errors && response.errors.length > 0) {
      console.error('GraphQL errors in clinic details:', response.errors);
      throw new Error(response.errors[0].message || 'GraphQL error occurred');
    }

    return response.data.customerSettings;
  } catch (error) {
    console.error('Error fetching clinic details:', error);
    throw error;
  }
};
